import { AbstractControl, ValidatorFn } from '@angular/forms';
import {
  DateUtil,
  IFormFieldValidation,
  ITravelDurationConfig,
  ValidationType,
} from '@nibtravel/shared/util/models';
import {
  addDays,
  differenceInDays,
  differenceInMonths,
  format,
  isValid,
} from 'date-fns';

export class DateValidators {
  static maxTripLengthValidation(
    dependentControlName: string,
    tripConfig: ITravelDurationConfig
  ): IFormFieldValidation {
    return {
      type: ValidationType.Custom,
      custom: {
        name: 'maxTripLength',
        validator: () =>
          DateValidators.maxTripLength(dependentControlName, tripConfig),
      },
      message: (ngControl: AbstractControl, value: any) =>
        `Trip length cannot exceed ${
          value.maxTripMonths
            ? value.maxTripMonths + ' month(s)'
            : value.maxTripDays + ' day(s)'
        }.`,
    };
  }

  static maxTripLength(
    dependentControlName: string,
    config: ITravelDurationConfig
  ): ValidatorFn {
    return (control: AbstractControl) => {
      const dependentControl = control.parent?.get(dependentControlName);
      if (!dependentControl) {
        return null;
      }

      const thisControlDate = control.value;
      const dependantControlDate = dependentControl.value;

      if (config.maxTravelDurationMonths) {
        const actualMonths = differenceInMonths(
          new Date(thisControlDate),
          new Date(dependantControlDate)
        );
        if (actualMonths >= config.maxTravelDurationMonths) {
          return {
            maxTripLength: {
              maxTripMonths: config.maxTravelDurationMonths,
              actualMonths,
            },
          };
        }
      } else if (config.maxTravelDurationDays) {
        const actualDays = differenceInDays(
          new Date(thisControlDate),
          new Date(dependantControlDate)
        );
        if (actualDays > config.maxTravelDurationDays) {
          return {
            maxTripLength: {
              maxTripDays: config.maxTravelDurationDays,
              actualDays,
            },
          };
        }
      }
      return null;
    };
  }

  static validDepartureDateValidation(): IFormFieldValidation {
    return {
      type: ValidationType.Custom,
      custom: {
        name: 'validDepartureDate',
        validator: this.validDepartureDate,
      },
      message: (ngControl: AbstractControl, value: any) => value,
    };
  }

  static validDepartureDate(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }

      const date = Date.parse(control.value);
      if (!isValid(date)) {
        return {
          validDepartureDate: 'Invalid date.',
        };
      }

      const departureDate = format(date, DateUtil.IsoDateFormat);
      if (!departureDate) {
        return {
          validDepartureDate: 'Invalid date format.',
        };
      }

      if (departureDate < format(new Date(), DateUtil.IsoDateFormat)) {
        return {
          validDepartureDate: 'Departure date in the past.',
        };
      }
      return null;
    };
  }

  static maxAdvancePurchaseValidation(
    tripConfig: ITravelDurationConfig
  ): IFormFieldValidation {
    return {
      type: ValidationType.Custom,
      custom: {
        name: 'maxAdvancePurchase',
        validator: () => DateValidators.maxAdvancePurchase(tripConfig),
      },
      message: (control: AbstractControl, value: any) =>
        `Sorry, you can't buy the standard policy if you're departing in more than ${value.maxDay} days time.`,
    };
  }

  static maxAdvancePurchase(tripConfig: ITravelDurationConfig): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }
      if (tripConfig.maxAdvancePurchaseDays) {
        const maxDate = addDays(new Date(), tripConfig.maxAdvancePurchaseDays);
        const departureDate = new Date(control.value);
        if (departureDate > maxDate) {
          return {
            maxAdvancePurchase: {
              maxDay: tripConfig.maxAdvancePurchaseDays,
            },
          };
        }
      }
      return null;
    };
  }
}
